import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
} from "@mui/material";
import {
  Edit as EditIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckCircleIcon,
  Visibility,
  VisibilityOff,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  CameraAlt as CameraAltIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services/authService";
import api from "../../services/api";
import { colaboradorService } from "../../services/colaboradorService";
import SessionSecurityPanel from "../../components/admin/SessionSecurityPanel";
import { useNavigate } from "react-router-dom";

const ColaboradorPerfil = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Estados de edición de perfil
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    nombre_completo: user?.colaborador?.nombre_completo || "",
    email: user?.email || "",
    telefono: user?.colaborador?.telefono || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Estado de foto de perfil
  const photoInputRef = useRef(null);
  const [fotoUrl, setFotoUrl] = useState(user?.colaborador?.foto_url || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Estados de cambio de contraseña
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // Estados de estadísticas
  const [stats, setStats] = useState([
    { label: "Eventos Asignados", value: "0", color: "#1565C0", icon: <EventIcon /> },
    { label: "Completados", value: "0", color: "#2e7d32", icon: <CheckCircleIcon /> },
    { label: "Horas Trabajadas", value: "0", color: "#c62828", icon: <TimeIcon /> },
    { label: "Ganancias", value: "$0", color: "#f57c00", icon: <MoneyIcon /> },
  ]);
  const [loadingStats, setLoadingStats] = useState(true);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const res = await api.get("/colaborador/dashboard");
      if (res.data?.success) {
        const s = (res.data.data || res.data)?.stats || {};
        setStats([
          { label: "Eventos Asignados", value: String(s.eventos_asignados || 0), color: "#1565C0", icon: <EventIcon /> },
          { label: "Completados",       value: String(s.eventos_completados || 0), color: "#2e7d32", icon: <CheckCircleIcon /> },
          { label: "Próximos Eventos",  value: String(s.eventos_proximos || 0), color: "#c62828", icon: <TimeIcon /> },
          { label: "Tareas Pendientes", value: String(s.tareas_pendientes || 0), color: "#f57c00", icon: <MoneyIcon /> },
        ]);
      }
    } catch {
      // No crítico
    } finally {
      setLoadingStats(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      // TODO: Implementar endpoint de actualización de perfil
      setSnackbar({ open: true, message: "Perfil actualizado correctamente", severity: "success" });
      setEditOpen(false);
    } catch {
      setSnackbar({ open: true, message: "Error al actualizar perfil", severity: "error" });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setPasswordError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      setSavingPassword(true);
      const response = await authService.changePassword(
        passwordData.oldPassword,
        passwordData.newPassword,
      );
      if (response?.data?.success) {
        setSnackbar({ open: true, message: "Contraseña cambiada correctamente", severity: "success" });
        setPasswordOpen(false);
        setPasswordData({ oldPassword: "", newPassword: "", confirmPassword: "" });
        setPasswordError("");
      } else {
        setPasswordError(response?.data?.message || "Error al cambiar contraseña");
      }
    } catch (err) {
      setPasswordError(err.response?.data?.message || "Error al cambiar contraseña");
    } finally {
      setSavingPassword(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Limpiar input para que vuelva a disparar onChange si se selecciona el mismo archivo
    if (photoInputRef.current) photoInputRef.current.value = "";
    try {
      setUploadingPhoto(true);
      const res = await colaboradorService.uploadPhoto(file);
      const newUrl = res?.url || res?.data?.url;
      if (!newUrl) throw new Error(res?.message || "No se pudo obtener la URL de la foto");
      // Persistir en la BD
      if (user?.colaborador?.id) {
        await api.put(`/colaboradores/${user.colaborador.id}`, { foto_url: newUrl });
      }
      // Actualizar estado local y sessionStorage para que persista en la sesión
      setFotoUrl(newUrl);
      try {
        const stored = JSON.parse(sessionStorage.getItem("user") || "{}");
        if (stored.colaborador) {
          stored.colaborador.foto_url = newUrl;
          sessionStorage.setItem("user", JSON.stringify(stored));
        }
      } catch {/* no crítico */}
      setSnackbar({ open: true, message: "✅ Foto de perfil actualizada", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error al subir la foto", severity: "error" });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>

      {/* ── Header con avatar y datos básicos ── */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: 4,
          background: "linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)",
        }}
      >
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          gap: { xs: 2, sm: 3 },
        }}>
          {/* Avatar con overlay de cámara */}
          <Box sx={{ position: "relative", flexShrink: 0, width: "fit-content" }}>
            <Avatar
              src={fotoUrl || undefined}
              sx={{
                width: { xs: 110, sm: 100 },
                height: { xs: 110, sm: 100 },
                fontSize: { xs: 44, sm: 40 },
                background: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)",
                color: "#000",
                fontWeight: 700,
                cursor: "pointer",
              }}
              onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
            >
              {user?.colaborador?.nombre_completo?.charAt(0) || user?.username?.charAt(0)}
            </Avatar>
            {/* Botón de cámara */}
            <Box
              onClick={() => !uploadingPhoto && photoInputRef.current?.click()}
              sx={{
                position: "absolute",
                bottom: 2,
                right: 2,
                width: 30,
                height: 30,
                borderRadius: "50%",
                bgcolor: "#1565C0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: uploadingPhoto ? "wait" : "pointer",
                border: "2px solid #0a0e27",
                "&:hover": { bgcolor: "#1976D2" },
              }}
            >
              {uploadingPhoto
                ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                : <CameraAltIcon sx={{ fontSize: 15, color: "#fff" }} />}
            </Box>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={handlePhotoUpload}
            />
          </Box>

          {/* Nombre + usuario + chips */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", sm: "left" }, width: { xs: "100%", sm: "auto" } }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#fff", mb: 0.5, fontSize: { xs: "1.4rem", sm: "2rem" } }}
            >
              {user?.colaborador?.nombre_completo || user?.username}
            </Typography>
            <Typography variant="body1" sx={{ color: "#90caf9", mb: 1.5 }}>
              @{user?.username}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "center", sm: "flex-start" } }}>
              <Chip
                label="Colaborador"
                color="primary"
                variant="outlined"
                size="small"
                sx={{ color: "#90caf9", borderColor: "#90caf9" }}
              />
              <Chip
                label="Activo"
                icon={<CheckCircleIcon sx={{ fontSize: "0.9rem !important" }} />}
                size="small"
                sx={{ bgcolor: "#2e7d32", color: "#fff" }}
              />
            </Box>
          </Box>

          {/* Botones */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              size="small"
              sx={{
                background: "linear-gradient(45deg, #00C9FF 0%, #92FE9D 100%)",
                color: "#000",
                fontWeight: "bold",
                borderRadius: "50px",
              }}
            >
              Editar Perfil
            </Button>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setPasswordOpen(true)}
              size="small"
              sx={{ borderColor: "#90caf9", color: "#90caf9", borderRadius: "50px" }}
            >
              Cambiar Contraseña
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size="small"
              sx={{ borderRadius: "50px" }}
            >
              Cerrar Sesión
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ── Panel de Seguridad de Sesiones (igual que el admin) ── */}
      <Box sx={{ mb: 4 }}>
        <SessionSecurityPanel />
      </Box>

      {/* ── Estadísticas — ocultas en móvil ── */}
      <Grid container spacing={2} sx={{ mb: 4, display: { xs: "none", sm: "flex" } }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card
              sx={{
                background: "rgba(255,255,255,0.05)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Box sx={{ color: stat.color, mb: 1 }}>{stat.icon}</Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: "block" }}>
                  {stat.label}
                </Typography>
                {loadingStats ? (
                  <CircularProgress size={20} />
                ) : (
                  <Typography variant="h5" sx={{ color: stat.color, fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Información Personal ── */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              📋 Información Personal
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box>
                <Typography variant="caption" color="textSecondary">Usuario</Typography>
                <Typography variant="body2">{user?.username}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Nombre Completo</Typography>
                <Typography variant="body2">{user?.colaborador?.nombre_completo || "—"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Email</Typography>
                <Typography variant="body2">{user?.email || "No proporcionado"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Teléfono</Typography>
                <Typography variant="body2">{user?.colaborador?.telefono || "No proporcionado"}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">Estado</Typography>
                <Typography variant="body2" sx={{ color: "#2e7d32", fontWeight: "bold" }}>
                  ✓ Activo
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Acciones Rápidas */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              ⚡ Acciones Rápidas
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "grid", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EventIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={() => navigate("/colaborador/eventos")}
              >
                Ver Mis Eventos
              </Button>
              <Button
                variant="outlined"
                startIcon={<MoneyIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={() => navigate("/colaborador/finanzas")}
              >
                Ver Mis Finanzas
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                fullWidth
                sx={{ justifyContent: "flex-start" }}
                onClick={handleLogout}
              >
                Cerrar Sesión
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Dialog: Editar Perfil ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Perfil</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Nombre Completo"
            value={editData.nombre_completo}
            onChange={(e) => setEditData((p) => ({ ...p, nombre_completo: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={editData.email}
            onChange={(e) => setEditData((p) => ({ ...p, email: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Teléfono"
            value={editData.telefono}
            onChange={(e) => setEditData((p) => ({ ...p, telefono: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={savingProfile}
            startIcon={savingProfile ? <CircularProgress size={16} /> : null}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Cambiar Contraseña ── */}
      <Dialog open={passwordOpen} onClose={() => setPasswordOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cambiar Contraseña</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: "16px !important" }}>
          {passwordError && <Alert severity="error">{passwordError}</Alert>}
          <TextField
            label="Contraseña Actual"
            type={showOldPassword ? "text" : "password"}
            value={passwordData.oldPassword}
            onChange={(e) => setPasswordData((p) => ({ ...p, oldPassword: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={() => setShowOldPassword((v) => !v)} sx={{ minWidth: "auto" }}>
                  {showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </Button>
              ),
            }}
          />
          <TextField
            label="Nueva Contraseña"
            type={showNewPassword ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData((p) => ({ ...p, newPassword: e.target.value }))}
            fullWidth
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={() => setShowNewPassword((v) => !v)} sx={{ minWidth: "auto" }}>
                  {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </Button>
              ),
            }}
          />
          <TextField
            label="Confirmar Nueva Contraseña"
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData((p) => ({ ...p, confirmPassword: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPasswordOpen(false); setPasswordError(""); }}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleChangePassword}
            disabled={savingPassword}
            startIcon={savingPassword ? <CircularProgress size={16} /> : null}
          >
            Cambiar Contraseña
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ColaboradorPerfil;
